import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, RotateCcw, X, ExternalLink, ShieldAlert, CheckCircle2 } from "lucide-react";
import useModalA11y from "@/lib/useModalA11y";

const STATUS_STYLE = {
  paid:      { bg: "rgba(34,197,94,.15)",  fg: "#22C55E", label: "Paid" },
  pending:   { bg: "rgba(245,158,11,.15)", fg: "#F59E0B", label: "Pending" },
  unpaid:    { bg: "rgba(245,158,11,.10)", fg: "#F59E0B", label: "Unpaid" },
  refunded:  { bg: "rgba(148,163,184,.14)",fg: "#94A3B8", label: "Refunded" },
  expired:   { bg: "rgba(239,68,68,.10)",  fg: "#EF4444", label: "Expired" },
  failed:    { bg: "rgba(239,68,68,.15)",  fg: "#EF4444", label: "Failed" },
};

const PAGE_SIZE = 25;

export default function AdminPayments() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refunding, setRefunding] = useState(null);   // tx being refunded
  const [refundNote, setRefundNote] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [busy, setBusy] = useState(false);
  const modalRef = useModalA11y({ open: !!refunding, onClose: () => setRefunding(null) });

  const load = async (nextOffset = offset) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/payments?limit=${PAGE_SIZE}&offset=${nextOffset}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
      setOffset(nextOffset);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load payments");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(0); /* eslint-disable-next-line */ }, []);

  const submitRefund = async () => {
    if (!refunding) return;
    setBusy(true);
    try {
      const body = {};
      if (refundReason)  body.reason = refundReason;
      if (refundAmount)  body.amount = Number(refundAmount);
      if (refundNote)    body.note = refundNote;
      const r = await api.post(`/admin/payments/${refunding.id}/refund`, body);
      toast.success(`Refunded £${r.data.amount.toFixed(2)}`);
      setRefunding(null);
      setRefundNote(""); setRefundAmount(""); setRefundReason("");
      load(offset);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally { setBusy(false); }
  };

  const totalPaid = rows.filter((r) => r.payment_status === "paid").reduce((s, r) => s + (r.amount || 0), 0);
  const totalRefunded = rows.filter((r) => r.payment_status === "refunded").reduce((s, r) => s + (r.refunded_amount || r.amount || 0), 0);

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="fl-label text-[#f47a20]">Admin</div>
          <h1 className="fl-heading text-4xl mt-2">Payments</h1>
          <p className="text-sm mt-2" style={{ color: "var(--fl-muted)" }}>Every checkout that reached Stripe · issue refunds inline.</p>
        </div>
        <button onClick={() => load(offset)} disabled={loading} className="fl-btn-ghost text-xs" data-testid="admin-payments-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""}/> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="fl-card p-4"><div className="fl-label">Total txns</div><div className="fl-heading text-xl mt-1">{total}</div></div>
        <div className="fl-card p-4"><div className="fl-label">Paid (this page)</div><div className="fl-heading text-xl mt-1" style={{ color: "#22C55E" }}>£{totalPaid.toFixed(2)}</div></div>
        <div className="fl-card p-4"><div className="fl-label">Refunded (this page)</div><div className="fl-heading text-xl mt-1" style={{ color: "#94A3B8" }}>£{totalRefunded.toFixed(2)}</div></div>
        <div className="fl-card p-4"><div className="fl-label">Showing</div><div className="fl-heading text-xl mt-1">{offset + 1}–{Math.min(offset + rows.length, total)}</div></div>
      </div>

      <div className="fl-card overflow-hidden" data-testid="admin-payments-table">
        <div className="grid grid-cols-12 gap-3 p-3 border-b text-[10px] uppercase tracking-wider font-bold" style={{ borderColor: "var(--fl-border)", color: "var(--fl-muted)" }}>
          <div className="col-span-3">User</div>
          <div className="col-span-3">Package</div>
          <div className="col-span-2 text-right">Amount</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {rows.map((r) => {
          const st = STATUS_STYLE[r.payment_status] || { bg: "rgba(148,163,184,.14)", fg: "#94A3B8", label: r.payment_status || "—" };
          const canRefund = r.payment_status === "paid" && r.stripe_payment_intent_id;
          return (
            <div key={r.id} className="grid grid-cols-12 gap-3 p-3 border-b text-sm items-center" style={{ borderColor: "var(--fl-border)" }} data-testid={`admin-payment-row-${r.id}`}>
              <div className="col-span-3 min-w-0">
                <div className="truncate font-medium" style={{ color: "var(--fl-text)" }}>{r.user_name || "—"}</div>
                <div className="text-[11px] truncate" style={{ color: "var(--fl-muted-2)" }}>{r.user_email}</div>
              </div>
              <div className="col-span-3 min-w-0">
                <div className="truncate">{r.package_name}</div>
                <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>{r.plan}</div>
              </div>
              <div className="col-span-2 text-right">
                <div className="font-medium">£{(r.amount || 0).toFixed(2)}</div>
                <div className="text-[10px]" style={{ color: "var(--fl-muted-2)" }}>{r.currency?.toUpperCase() || "GBP"}</div>
              </div>
              <div className="col-span-2">
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full inline-flex items-center gap-1"
                      style={{ background: st.bg, color: st.fg, border: `1px solid ${st.fg}55` }}
                      data-testid={`admin-payment-status-${r.id}`}>
                  {st.label}
                </span>
                <div className="text-[10px] mt-1" style={{ color: "var(--fl-muted-2)" }}>{new Date(r.created_at).toLocaleDateString("en-GB")}</div>
              </div>
              <div className="col-span-2 text-right">
                {canRefund && (
                  <button
                    onClick={() => { setRefundAmount(""); setRefundReason(""); setRefundNote(""); setRefunding(r); }}
                    className="fl-btn-ghost text-xs"
                    style={{ color: "#EF4444" }}
                    data-testid={`admin-payment-refund-${r.id}`}
                  >
                    <RotateCcw size={12} /> Refund
                  </button>
                )}
                {r.payment_status === "refunded" && (
                  <span className="text-[10px] inline-flex items-center gap-1" style={{ color: "var(--fl-muted-2)" }}>
                    <CheckCircle2 size={10}/> Done
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {!loading && rows.length === 0 && (
          <div className="p-8 text-center text-sm" style={{ color: "var(--fl-muted-2)" }}>No payments yet.</div>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <button onClick={() => load(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0 || loading}
                  className="fl-btn-ghost text-xs" data-testid="admin-payments-prev">← Previous</button>
          <span style={{ color: "var(--fl-muted)" }}>{offset + 1}–{Math.min(offset + rows.length, total)} of {total}</span>
          <button onClick={() => load(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total || loading}
                  className="fl-btn-ghost text-xs" data-testid="admin-payments-next">Next →</button>
        </div>
      )}

      {refunding && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-auto" onClick={() => setRefunding(null)}>
          <div ref={modalRef} role="dialog" aria-modal="true"
               className="fl-card fl-card-elevated p-6 w-full max-w-lg"
               onClick={(e) => e.stopPropagation()}
               data-testid="admin-refund-modal">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="fl-label text-[#f47a20]">Refund</div>
                <div className="fl-heading text-xl mt-1">{refunding.package_name}</div>
                <div className="text-xs mt-1" style={{ color: "var(--fl-muted-2)" }}>
                  {refunding.user_email} · £{(refunding.amount || 0).toFixed(2)} {refunding.currency?.toUpperCase() || "GBP"}
                </div>
              </div>
              <button onClick={() => setRefunding(null)} aria-label="Close" data-testid="admin-refund-close"><X size={16} className="text-zinc-400"/></button>
            </div>

            <div className="fl-card p-3 mb-4 text-xs flex items-start gap-2" style={{ color: "#F59E0B", border: "1px solid rgba(245,158,11,.4)" }}>
              <ShieldAlert size={14} className="shrink-0 mt-0.5"/>
              This will trigger a real Stripe refund. Membership access is revoked automatically via the `charge.refunded` webhook.
            </div>

            <div className="space-y-3 mb-4">
              <div>
                <div className="fl-label mb-1">Amount (optional — leave empty for full refund)</div>
                <input type="number" step="0.01" min="0.01"
                       max={refunding.amount}
                       placeholder={`Full: £${(refunding.amount || 0).toFixed(2)}`}
                       className="fl-input"
                       value={refundAmount}
                       onChange={(e) => setRefundAmount(e.target.value)}
                       data-testid="admin-refund-amount" />
              </div>
              <div>
                <div className="fl-label mb-1">Reason</div>
                <select className="fl-input" value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        data-testid="admin-refund-reason">
                  <option value="">Customer request (default)</option>
                  <option value="duplicate">Duplicate charge</option>
                  <option value="fraudulent">Fraudulent</option>
                </select>
              </div>
              <div>
                <div className="fl-label mb-1">Internal note (optional)</div>
                <textarea rows={2} className="fl-input"
                          value={refundNote}
                          onChange={(e) => setRefundNote(e.target.value)}
                          placeholder="Why this refund was issued (not shown to customer)"
                          data-testid="admin-refund-note" />
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={submitRefund} disabled={busy}
                      className="fl-btn flex-1 justify-center"
                      style={{ background: "#EF4444", borderColor: "#EF4444" }}
                      data-testid="admin-refund-submit">
                <RotateCcw size={14}/> {busy ? "Refunding…" : "Issue refund"}
              </button>
              <button onClick={() => setRefunding(null)} className="fl-btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
